// Traduz quadro de aparelho em leitura pronta para a API.
//
// Funcao pura de proposito: e aqui que mora o risco (datar errado, duplicar,
// perder medida), e regra que roda sem navegador e regra que da para testar.

import {
  decodificarLinkSun, decodificarYk, decodificarFc,
  type TipoLeitura, type OrigemLeitura,
} from '@medicov1/shared';

export interface LeituraParaEnviar {
  tipo: TipoLeitura;
  valor: number;
  origem: OrigemLeitura;
  /** Quando a medida aconteceu, ja corrigida. */
  medidoEm: string;
  /** O que o relogio do aparelho dizia. Metadado — costuma estar errado. */
  medidoEmAparelho?: string;
  /** Une as medidas de um mesmo evento (sistolica + diastolica + pulso). */
  grupo?: string;
  bruto?: string;
  /** Verdadeiro quando veio da memoria do aparelho, nao da medicao do momento. */
  daMemoria?: boolean;
  /** Aparelho cadastrado que originou a leitura, preenchido na hora de enfileirar. */
  aparelhoId?: string;
}

/**
 * O relogio interno dos aparelhos esta errado — o glicosimetro marca 2024, o
 * monitor de pressao marca 2025. Mas o erro e CONSTANTE: a diferenca entre duas
 * medidas do mesmo aparelho continua valendo.
 *
 * Entao, quando chega uma medida do momento, aprendemos o desvio daquele
 * relogio. Com ele, as medidas guardadas na memoria do aparelho podem ser
 * datadas corretamente em vez de se empilharem todas no instante da conexao.
 */
export function desvioDoRelogio(medidoEmAparelho: Date, recebidoEm: Date): number {
  return recebidoEm.getTime() - medidoEmAparelho.getTime();
}

/** Aplica um desvio aprendido. Sem desvio conhecido, devolve null. */
export function corrigirPeloDesvio(medidoEmAparelho: Date, desvioMs: number | null): Date | null {
  if (desvioMs === null) return null;
  return new Date(medidoEmAparelho.getTime() + desvioMs);
}

// ------------------------------------------------ memoria do desvio, no tablet
//
// O desvio precisa sobreviver a recarregar a pagina e a esquecer o aparelho.
// Motivo pratico, descoberto testando: o YK-IBPA1 se desliga sozinho e JA COMECA
// A MEDIR quando religa — o sistema raramente esta conectado a tempo de pegar a
// medida ao vivo. Ela chega pela memoria do aparelho na conexao seguinte. Sem o
// desvio guardado, essa medida seria descartada, e o aparelho praticamente nao
// funcionaria no uso real.

const CHAVE_DESVIO = 'medicov1:desvio:';

export function guardarDesvio(aparelho: string, desvioMs: number): void {
  try {
    localStorage.setItem(CHAVE_DESVIO + aparelho, String(desvioMs));
  } catch { /* sem storage: vale so nesta sessao */ }
}

export function lerDesvio(aparelho: string): number | null {
  try {
    const guardado = localStorage.getItem(CHAVE_DESVIO + aparelho);
    if (guardado === null) return null;
    const desvio = Number(guardado);
    return Number.isFinite(desvio) ? desvio : null;
  } catch {
    return null;
  }
}

const hex = (dados: Uint8Array) =>
  Array.from(dados).map((b) => b.toString(16).padStart(2, '0')).join(' ');

// ------------------------------------------------------------- glicosimetro

export function traduzirLinkSun(dados: Uint8Array, recebidoEm = new Date()): LeituraParaEnviar | null {
  const quadro = decodificarLinkSun(dados);
  if (!quadro || quadro.tipo !== 'resultado') return null;

  // Checksum divergente nao entra: numero errado com cara de certo e pior que
  // numero nenhum (docs/BUSINESS_RULES.md).
  if (!quadro.checksumOk) return null;

  return {
    tipo: 'glicemia',
    valor: quadro.valor,
    origem: 'ble',
    // O LinkSun so transmite ao medir: o que chega aconteceu agora.
    medidoEm: recebidoEm.toISOString(),
    medidoEmAparelho: quadro.medidoEmAparelho.toISOString(),
    bruto: hex(dados),
  };
}

// ----------------------------------------------------------------- pressao

export interface ResultadoYk {
  leituras: LeituraParaEnviar[];
  /** Desvio aprendido nesta medida, se ela for do momento. */
  desvioAprendido: number | null;
}

/**
 * Uma medida de pressao vira TRES leituras com o mesmo grupo. O YK despeja as 8
 * ultimas da memoria ao conectar; cada uma e datada pelo desvio aprendido, e
 * marcada como vinda da memoria.
 */
export function traduzirYk(
  dados: Uint8Array,
  recebidoEm = new Date(),
  desvioConhecido: number | null = null,
): ResultadoYk {
  const quadro = decodificarYk(dados);
  if (!quadro || quadro.tipo !== 'resultado') return { leituras: [], desvioAprendido: null };

  // Medida do momento (indice 0) ensina o desvio daquele relogio.
  const desvioAprendido = quadro.daMemoria ? null : desvioDoRelogio(quadro.medidoEmAparelho, recebidoEm);
  const desvio = desvioAprendido ?? desvioConhecido;

  const quando = quadro.daMemoria
    ? corrigirPeloDesvio(quadro.medidoEmAparelho, desvio)
    : recebidoEm;

  // Sem desvio conhecido nao da para datar o que veio da memoria. Gravar com a
  // hora da conexao criaria oito medidas falsas no mesmo minuto — pior que nao
  // gravar. Elas voltam na proxima conexao, depois de uma medida do momento.
  if (!quando) return { leituras: [], desvioAprendido: null };

  const grupo = `yk-${quando.getTime()}`;
  const comum = {
    origem: 'ble' as const,
    medidoEm: quando.toISOString(),
    medidoEmAparelho: quadro.medidoEmAparelho.toISOString(),
    grupo,
    daMemoria: quadro.daMemoria,
  };

  return {
    desvioAprendido,
    leituras: [
      { ...comum, tipo: 'pa_sistolica', valor: quadro.sistolica, bruto: hex(dados) },
      { ...comum, tipo: 'pa_diastolica', valor: quadro.diastolica },
      { ...comum, tipo: 'frequencia_cardiaca', valor: quadro.pulso },
    ],
  };
}

// ------------------------------------------------------ frequencia cardiaca

/**
 * A pulseira manda bpm varias vezes por segundo. Guardar tudo encheria o banco
 * sem informar mais nada, entao so passa quem muda o bastante ou espera demais.
 */
export interface EstadoFc {
  ultimoValor: number | null;
  ultimoEnvioMs: number;
}

export const FC_INTERVALO_MINIMO_MS = 30_000;
export const FC_VARIACAO_RELEVANTE = 5;

export function traduzirFc(
  dados: DataView,
  estado: EstadoFc,
  recebidoEm = new Date(),
): { leitura: LeituraParaEnviar | null; estado: EstadoFc } {
  const medida = decodificarFc(dados);
  if (!medida) return { leitura: null, estado };

  // Sensor que reporta contato e diz que nao ha: a pulseira saiu do braco.
  if (medida.contatoPele === false) return { leitura: null, estado };

  const agora = recebidoEm.getTime();
  const mudouBastante =
    estado.ultimoValor === null || Math.abs(medida.bpm - estado.ultimoValor) >= FC_VARIACAO_RELEVANTE;
  const esperouBastante = agora - estado.ultimoEnvioMs >= FC_INTERVALO_MINIMO_MS;

  if (!mudouBastante && !esperouBastante) {
    return { leitura: null, estado: { ...estado, ultimoValor: medida.bpm } };
  }

  return {
    leitura: {
      tipo: 'frequencia_cardiaca',
      valor: medida.bpm,
      origem: 'ble',
      medidoEm: recebidoEm.toISOString(),
    },
    estado: { ultimoValor: medida.bpm, ultimoEnvioMs: agora },
  };
}
